import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase, isLocalStack } from '@/lib/supabase';
import { LoginScreen } from '@/features/auth/LoginScreen';
import { Dashboard } from '@/features/dashboard/Dashboard';
import { RequestTimeOff } from '@/features/request/RequestTimeOff';

/**
 * The shell. Checked in this order:
 *
 *  1. #/request — the OPEN employee request form (M2, P3). No session needed,
 *     checked BEFORE the login gate on purpose: employees have no accounts,
 *     and this page is the whole reason anon can call two RPCs and nothing
 *     else. A signed-in manager can open it too (that is how it gets tested).
 *  2. Signed in (and not on #/request) — the portal (Dashboard, Log an
 *     Absence, Manage Records, Roster, or the Admin Console), regardless
 *     of hash — Dashboard.tsx's own router resolves which.
 *  3. Signed out, no explicit #/login — the employee request form. Pre-P8
 *     punch list, Tier 1: with ~10 employees and 1-2 managers, the bare URL
 *     should default to the form employees actually need, not manager
 *     login. The two screens are already cross-linked (Login → "Use the
 *     request form"; the request form's footer → "manager sign-in").
 *  4. Signed out, #/login — the manager login screen.
 */
export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [onRequestPage, setOnRequestPage] = useState(isRequestHash());
  const [onLoginPage, setOnLoginPage] = useState(isLoginHash());

  useEffect(() => {
    // Existing session, if the browser already has one.
    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    // And react to sign-in / sign-out / token refresh from here on.
    const { data } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });

    return () => data.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const onHash = () => {
      setOnRequestPage(isRequestHash());
      setOnLoginPage(isLoginHash());
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  if (onRequestPage) {
    return (
      <div style={S.shell}>
        <EnvironmentBanner />
        <RequestTimeOff />
      </div>
    );
  }

  // Wait for the session check before picking a default — deciding on
  // `session` while it's still loading would flash the request form at a
  // returning, already-signed-in manager before flipping to their dashboard.
  if (loading) return <main style={S.center}>Loading…</main>;

  if (session) {
    return (
      // The live portal's page background, verbatim (--bg: #F3F5F8 in every
      // Apps Script shell) — white cards float on a cool blue-tinted field,
      // so the two portals read as the same product. Scott's call, 2026-08-11.
      <div style={S.shell}>
        <EnvironmentBanner />
        <Dashboard session={session} />
      </div>
    );
  }

  if (!onLoginPage) {
    return (
      <div style={S.shell}>
        <EnvironmentBanner />
        <RequestTimeOff />
      </div>
    );
  }

  return (
    <div style={S.shell}>
      <EnvironmentBanner />
      <LoginScreen />
    </div>
  );
}

function isRequestHash(): boolean {
  return window.location.hash === '#/request';
}

function isLoginHash(): boolean {
  return window.location.hash === '#/login';
}

/**
 * Which database this tab is pointed at — shown ONLY on the local stack.
 *
 * From P0 until 2026-09-10 this rendered on both: blue "LOCAL" and amber
 * "HOSTED — the real allied-pto-portal project". The hosted half made sense
 * while the portal was a build in progress and a developer might have two
 * tabs open against two databases. Since the cutover (2026-09-08) hosted is
 * simply the portal, staff are its audience, and a banner announcing that the
 * real system is real is noise — Scott asked for it to go, two days after
 * go-live. The LOCAL banner stays: "I thought I was on local" is still how
 * someone edits real PTO records while poking at a half-built feature, and
 * the local stack is the only place that mistake can start.
 */
function EnvironmentBanner() {
  if (!isLocalStack) return null;
  return (
    <div style={{ ...S.banner, background: '#e8f0fe', borderBottom: '1px solid #c6d9f7' }}>
      LOCAL — disposable database on this machine
    </div>
  );
}

const S = {
  shell: { minHeight: '100vh', background: '#F3F5F8' },
  center: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'system-ui, sans-serif',
    color: '#666',
  },
  banner: {
    padding: '0.4rem 1rem',
    fontFamily: 'system-ui, sans-serif',
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.03em',
    textAlign: 'center',
    color: '#333',
  },
} satisfies Record<string, React.CSSProperties>;
